import { v2, vDist, type Vec2 } from "../math";
import type { AsteroidDef, VineDef, WorldDef } from "./types";

/**
 * Vines between the lobes of a vined rock (see VineDef): their shape, as a
 * curve both server and client work out the same from the defs, so climbing
 * one agrees everywhere and nothing about them is ever sent but whether
 * they're cut.
 */

/** A vine's curve: a quadratic from `a` to `b`, pulled toward `c`. */
export interface VineCurve {
  a: Vec2;
  c: Vec2;
  b: Vec2;
}

/**
 * Each vine's rock (found again only when the world's rocks change), its
 * curve, and where the rock was when it was worked out (a rock that drifts
 * takes its vines with it).
 */
const curves = new WeakMap<VineDef, { rocks: readonly AsteroidDef[]; rock?: AsteroidDef; at: string; curve: VineCurve | null }>();

/** A vine's curve (null if its rock isn't in this world). */
export function vineCurve(world: WorldDef, v: VineDef): VineCurve | null {
  let had = curves.get(v);
  if (had?.rocks !== world.asteroids) {
    had = { rocks: world.asteroids, rock: world.asteroids.find((x) => x.id === v.rock), at: "", curve: null };
    curves.set(v, had);
  }
  const { rock } = had;
  const at = `${rock?.ox},${rock?.oy}`;
  if (had.at === at) return had.curve;
  let c: VineCurve | null = null;
  if (rock) {
    const mx = rock.ox + rock.w / 2;
    const my = rock.oy + rock.h / 2;
    const a = v2(mx + v.a.x, my + v.a.y);
    const b = v2(mx + v.b.x, my + v.b.y);
    // Slack: the middle pulled aside, one way or the other by its seed.
    const len = vDist(a, b) || 1;
    const side = v.seed & 1 ? 1 : -1;
    const nx = (-(b.y - a.y) / len) * side;
    const ny = ((b.x - a.x) / len) * side;
    c = { a, b, c: v2((a.x + b.x) / 2 + nx * v.sag * 2, (a.y + b.y) / 2 + ny * v.sag * 2) };
  }
  had.at = at;
  had.curve = c;
  return c;
}

/** The point at `t` (0 at the first anchor, 1 at the other end) along a vine. */
export function vinePoint(c: VineCurve, t: number): Vec2 {
  const u = 1 - t;
  return v2(u * u * c.a.x + 2 * u * t * c.c.x + t * t * c.b.x, u * u * c.a.y + 2 * u * t * c.c.y + t * t * c.b.y);
}

/** Which way a vine runs at `t` (a unit vector toward its far end). */
export function vineTangent(c: VineCurve, t: number): Vec2 {
  const x = 2 * (1 - t) * (c.c.x - c.a.x) + 2 * t * (c.b.x - c.c.x);
  const y = 2 * (1 - t) * (c.c.y - c.a.y) + 2 * t * (c.b.y - c.c.y);
  const l = Math.hypot(x, y) || 1;
  return v2(x / l, y / l);
}

/** About how long a vine is, in blocks. */
export function vineLength(c: VineCurve): number {
  let len = 0;
  let p = c.a;
  for (let i = 1; i <= 16; i++) {
    const q = vinePoint(c, i / 16);
    len += vDist(p, q);
    p = q;
  }
  return len;
}

/** The nearest point of a vine to `p`: where along it (t) and how far away. */
export function nearestOnVine(c: VineCurve, p: Vec2): { t: number; d: number } {
  let best = 0;
  let bestD = Infinity;
  for (let i = 0; i <= 32; i++) {
    const d = vDist(vinePoint(c, i / 32), p);
    if (d < bestD) [best, bestD] = [i / 32, d];
  }
  // Refine between the neighbouring samples.
  let lo = Math.max(0, best - 1 / 32);
  let hi = Math.min(1, best + 1 / 32);
  for (let k = 0; k < 12; k++) {
    const m1 = lo + (hi - lo) / 3;
    const m2 = hi - (hi - lo) / 3;
    if (vDist(vinePoint(c, m1), p) < vDist(vinePoint(c, m2), p)) hi = m2;
    else lo = m1;
  }
  const t = (lo + hi) / 2;
  return { t, d: vDist(vinePoint(c, t), p) };
}

/** The uncut vine nearest `p` within `reach`, if any: it, its curve, and where along it. */
export function vineNear(world: WorldDef, cut: ReadonlySet<string>, p: Vec2, reach: number): { vine: VineDef; curve: VineCurve; t: number; d: number } | null {
  let best: { vine: VineDef; curve: VineCurve; t: number; d: number } | null = null;
  for (const vine of world.vines ?? []) {
    if (cut.has(vine.id)) continue;
    const curve = vineCurve(world, vine);
    if (!curve) continue;
    // Quick reject: further from both ends and the pull point than the vine is long.
    const span = vDist(curve.a, curve.b) + vDist(curve.a, curve.c);
    if (vDist(p, curve.a) > span + reach) continue;
    const near = nearestOnVine(curve, p);
    if (near.d <= reach && (!best || near.d < best.d)) best = { vine, curve, ...near };
  }
  return best;
}
